'use client';

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import {
  Plus,
  RefreshCw,
  Edit,
  Trash2,
  FolderTree,
  Loader2,
  X,
  Save,
  AlertCircle,
} from 'lucide-react';
import { toast } from '@/lib/toast';
import { useConfirm } from '@/components/common/feedback/ConfirmProvider';
import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  TicketCategory,
  TicketPriority,
  CreateCategoryParams,
  getPriorityLabel,
  ApiClientError,
} from '@/services/api/tickets';

/**
 * 分类管理页面
 * 遵循飞书风格设计规范
 */
export default function CategoriesManagePage() {
  const { t } = useTranslation();
  const confirm = useConfirm();

  // 状态
  const [categories, setCategories] = useState<TicketCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // 编辑/新建弹窗
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<TicketCategory | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // 表单状态
  const [formData, setFormData] = useState<CreateCategoryParams>({
    name: '',
    code: '',
    description: '',
    defaultPriority: TicketPriority.MEDIUM,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // 加载分类
  const loadCategories = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const data = await getCategories();
      setCategories(data);
    } catch (error) {
      if (error instanceof ApiClientError) {
        toast.error(error.message);
      } else {
        toast.error(t.common?.loadFailed || '加载失败');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // 打开新建弹窗
  const handleAdd = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      defaultPriority: TicketPriority.MEDIUM,
    });
    setErrors({});
    setShowModal(true);
  };

  // 打开编辑弹窗
  const handleEdit = (category: TicketCategory) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      code: category.code,
      description: category.description || '',
      defaultPriority: category.defaultPriority || TicketPriority.MEDIUM,
    });
    setErrors({});
    setShowModal(true);
  };

  // 删除分类
  const handleDelete = async (category: TicketCategory) => {
    const confirmed = await confirm({
      title: `确定删除分类 "${category.name}" 吗？`,
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await deleteCategory(category.id);
      toast.success(t.common?.deleteSuccess || '删除成功');
      loadCategories(true);
    } catch (error) {
      if (error instanceof ApiClientError) {
        toast.error(error.message);
      } else {
        toast.error(t.common?.deleteFailed || '删除失败');
      }
    }
  };

  // 验证表单
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t.tickets?.validation?.categoryNameRequired || '请输入分类名称';
    }

    if (!formData.code.trim()) {
      newErrors.code = t.tickets?.validation?.categoryCodeRequired || '请输入分类编码';
    } else if (!/^[A-Z][A-Z0-9_]*$/.test(formData.code)) {
      newErrors.code = t.tickets?.validation?.categoryCodeFormat || '编码必须以大写字母开头，只能包含大写字母、数字和下划线';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 提交表单
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);

      if (editingCategory) {
        await updateCategory(editingCategory.id, formData);
        toast.success(t.common?.updateSuccess || '更新成功');
      } else {
        await createCategory(formData);
        toast.success(t.common?.createSuccess || '创建成功');
      }

      setShowModal(false);
      loadCategories(true);
    } catch (error) {
      if (error instanceof ApiClientError) {
        if (error.code === 'TICKET_CATEGORY_CODE_EXISTS') {
          setErrors({ code: t.tickets?.validation?.codeExists || '编码已存在' });
        } else {
          toast.error(error.message);
        }
      } else {
        toast.error(t.common?.saveFailed || '保存失败');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !refreshing) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" style={{ color: '#3370ff' }} />
          <p className="mt-4 text-sm" style={{ color: '#8f959e' }}>
            {'加载中...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#f7f8fa' }}>
      {/* 标题栏 - 飞书风格，标准 h-12 高度 */}
      <div className="bg-white border-b" style={{ borderColor: '#e5e6eb' }}>
        <div className="px-6 h-12">
          <div className="flex items-center justify-between h-full">
            {/* 左侧：标题 */}
              <span className="text-sm font-medium" style={{ color: '#1f2329' }}>
              {t.tickets?.admin?.categories || '分类管理'}
            </span>

            {/* 右侧：操作按钮 */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => loadCategories(true)}
                disabled={refreshing}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
                style={{
                  backgroundColor: '#f7f8fa',
                  color: '#1f2329'
                }}
                onMouseEnter={(e) => {
                  if (!refreshing) e.currentTarget.style.backgroundColor = '#eff0f2';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f7f8fa';
                }}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                {t.common?.refresh || '刷新'}
              </button>

              <button
                onClick={handleAdd}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
                style={{
                  backgroundColor: '#3370ff',
                  color: 'white'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#1e5eff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#3370ff';
                }}
              >
                <Plus className="w-4 h-4" />
                新建分类
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 内容区域 - flex-1 自动占满剩余空间 */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {/* 分类列表卡片 */}
          <div className="bg-white rounded-lg" style={{ border: '1px solid #e5e6eb' }}>
            {categories.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#f7f8fa' }}>
                  <FolderTree className="w-10 h-10" style={{ color: '#c9cdd4' }} />
                </div>
                <h3 className="text-base font-medium mb-1" style={{ color: '#646a73' }}>
                  暂无分类
                </h3>
                <p className="text-sm mb-4" style={{ color: '#8f959e' }}>
                  请先创建工单分类
                </p>
                <button
                  onClick={handleAdd}
                  className="px-4 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2 mx-auto"
                  style={{
                    backgroundColor: '#3370ff',
                    color: '#ffffff'
                  }}
                >
                  <Plus className="w-4 h-4" />
                  新建分类
                </button>
              </div>
            ) : (
              <table className="w-full">
                {/* 表头 */}
                <thead 
                  className="border-b" 
                  style={{ 
                    borderColor: '#e5e6eb', 
                    backgroundColor: '#fafafa' 
                  }}
                >
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium" style={{ color: '#8f959e' }}>
                      名称
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium" style={{ color: '#8f959e' }}>
                      编码
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium" style={{ color: '#8f959e' }}>
                      默认优先级
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium" style={{ color: '#8f959e' }}>
                      状态
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium" style={{ color: '#8f959e' }}>
                      操作
                    </th>
                  </tr>
                </thead>

                {/* 表格内容 */}
                <tbody>
                  {categories.map((category, index) => (
                    <tr
                      key={category.id}
                      className="border-b transition-colors"
                      style={{
                        borderColor: index === categories.length - 1 ? 'transparent' : '#e5e6eb'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#fafafa';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#ffffff';
                      }}
                    >
                      <td className="px-6 py-4">
                        <div>
                          <span className="font-medium text-sm" style={{ color: '#1f2329' }}>
                            {category.name}
                          </span>
                          {category.description && (
                            <p className="text-xs mt-0.5 truncate max-w-xs" style={{ color: '#8f959e' }}>
                              {category.description}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <code 
                          className="text-xs px-2 py-0.5 rounded font-mono"
                          style={{
                            backgroundColor: 'rgba(51, 112, 255, 0.1)',
                            color: '#3370ff'
                          }}
                        >
                          {category.code}
                        </code>
                      </td>
                      <td className="px-6 py-4">
                        <span 
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                          style={{
                            backgroundColor: 'rgba(134, 144, 156, 0.1)',
                            color: '#646a73'
                          }}
                        >
                          {getPriorityLabel(category.defaultPriority || TicketPriority.MEDIUM, t)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span 
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                          style={{
                            backgroundColor: category.isActive ? 'rgba(0, 180, 42, 0.1)' : 'rgba(134, 144, 156, 0.1)',
                            color: category.isActive ? '#00b42a' : '#86909c'
                          }}
                        >
                          {category.isActive ? (t.common?.active || '启用') : (t.common?.inactive || '停用')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleEdit(category)}
                            className="p-1.5 rounded transition-all"
                            style={{ color: '#646a73' }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f7f8fa';
                              e.currentTarget.style.color = '#3370ff';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = '#646a73';
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(category)}
                            className="p-1.5 rounded transition-all"
                            style={{ color: '#646a73' }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'rgba(245, 63, 63, 0.1)';
                              e.currentTarget.style.color = '#f53f3f';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = '#646a73';
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* 编辑/新建弹窗 */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4" style={{ border: '1px solid #e5e6eb' }}>
            {/* 对话框标题 */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-base font-semibold" style={{ color: '#1f2329' }}>
                {editingCategory ? (t.common?.edit || '编辑') + (t.tickets?.field?.category || '分类') : (t.common?.create || '新建') + (t.tickets?.field?.category || '分类')}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-1 rounded transition-colors"
                style={{ color: '#8f959e' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#1f2329';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#8f959e';
                }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* 表单 */}
            <div className="space-y-4">
              {/* 名称 */}
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: '#1f2329' }}>
                  名称 <span className="text-red-500">*</span>
                </label>
                <input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="请输入分类名称"
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-all"
                  style={{
                    backgroundColor: '#ffffff',
                    border: errors.name ? '1px solid #f53f3f' : '1px solid #e5e6eb',
                    color: '#1f2329'
                  }}
                  onFocus={(e) => {
                    if (!errors.name) {
                      e.target.style.borderColor = '#3370ff';
                      e.target.style.boxShadow = '0 0 0 2px rgba(51, 112, 255, 0.1)';
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.name) {
                      e.target.style.borderColor = '#e5e6eb';
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                />
                {errors.name && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.name}
                  </p>
                )}
              </div>

              {/* 编码 */}
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: '#1f2329' }}>
                  编码 <span className="text-red-500">*</span>
                </label>
                <input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="如 BUG_REPORT"
                  disabled={!!editingCategory}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-all"
                  style={{
                    backgroundColor: editingCategory ? '#f7f8fa' : '#ffffff',
                    border: errors.code ? '1px solid #f53f3f' : '1px solid #e5e6eb',
                    color: editingCategory ? '#8f959e' : '#1f2329',
                    cursor: editingCategory ? 'not-allowed' : 'text'
                  }}
                  onFocus={(e) => {
                    if (!errors.code && !editingCategory) {
                      e.target.style.borderColor = '#3370ff';
                      e.target.style.boxShadow = '0 0 0 2px rgba(51, 112, 255, 0.1)';
                    }
                  }}
                  onBlur={(e) => {
                    if (!errors.code) {
                      e.target.style.borderColor = '#e5e6eb';
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                />
                {errors.code && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.code}
                  </p>
                )}
              </div>

              {/* 描述 */}
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: '#1f2329' }}>
                  描述
                </label>
                <input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t.common?.optional || '可选'}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-all"
                  style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e6eb',
                    color: '#1f2329'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3370ff';
                    e.target.style.boxShadow = '0 0 0 2px rgba(51, 112, 255, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e6eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* 默认优先级 */}
              <div className="space-y-2">
                <label className="block text-sm font-medium" style={{ color: '#1f2329' }}>
                  默认优先级
                </label>
                <select
                  value={formData.defaultPriority}
                  onChange={(e) => setFormData({ ...formData, defaultPriority: e.target.value as TicketPriority })}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-all cursor-pointer"
                  style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e6eb',
                    color: '#1f2329'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3370ff';
                    e.target.style.boxShadow = '0 0 0 2px rgba(51, 112, 255, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e6eb';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  {Object.values(TicketPriority).map((p) => (
                    <option key={p} value={p}>{getPriorityLabel(p, t)}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* 对话框底部按钮 */}
            <div className="flex justify-end gap-2 mt-6 pt-4" style={{ borderTop: '1px solid #e5e6eb' }}>
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-1.5 rounded-lg text-sm font-medium transition-all"
                style={{
                  backgroundColor: '#f7f8fa',
                  color: '#1f2329'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#eff0f2';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f7f8fa';
                }}
              >
                取消
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-all"
                style={{
                  backgroundColor: submitting ? '#c9cdd4' : '#3370ff',
                  color: '#ffffff',
                  cursor: submitting ? 'not-allowed' : 'pointer'
                }}
                onMouseEnter={(e) => {
                  if (!submitting) e.currentTarget.style.backgroundColor = '#1e5eff';
                }}
                onMouseLeave={(e) => {
                  if (!submitting) e.currentTarget.style.backgroundColor = '#3370ff';
                }}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    保存中...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    保存
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
